import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { MailIcon, PauseCircleIcon } from '@/assets/icons';
import { Button } from '@/components';
import {
  DELIVERY_PAUSED_REASONS,
  DeliveryPausedReason,
  useGetOrganizationQuery,
} from '@/store/api/organization';

const isKnownPausedReason = (value: string): value is DeliveryPausedReason => {
  return DELIVERY_PAUSED_REASONS.includes(value as DeliveryPausedReason);
};

const resolvePausedReasonKey = (
  pausedReason: DeliveryPausedReason | undefined
): DeliveryPausedReason => {
  if (pausedReason && isKnownPausedReason(pausedReason)) {
    return pausedReason;
  }
  return 'manual';
};

export const PausedDeliveryBanner: FC = () => {
  const { t } = useTranslation('components');
  const { data: organizationData } = useGetOrganizationQuery();

  const deliveryStatus = organizationData?.deliveryStatus;
  if (!deliveryStatus || deliveryStatus.status !== 'paused') {
    return null;
  }

  const pausedReasonKey = resolvePausedReasonKey(deliveryStatus?.pausedReason);

  return (
    <div className="w-full border-b border-orange-200 bg-orange-50 px-5 py-4">
      <div className="flex items-start gap-3">
        <PauseCircleIcon width={20} height={20} className="shrink-0 text-custom-orange-700" />
        <div className="w-full flex items-center justify-between gap-6">
          <div>
            <p className="text-body-16-semibold text-custom-orange-700 mb-2">
              {t('deliveryPausedBanner.title')}
            </p>
            <p className="text-body-14-regular text-custom-warm-grey-500">
              {t(`deliveryPausedBanner.description.${pausedReasonKey}`)}
            </p>
          </div>

          <div>
            <Button as="a" href="mailto:support@chirpee.co" variant="secondary" size="sm">
              <MailIcon width={20} height={20} />
              {t('deliveryPausedBanner.contactSupport')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
